// src/oura/components/site/MachineReadCard.tsx
//
// What a machine can read: the five checks AI Readability is the mean of,
// each as pages covered out of the pages it applies to (coloured by its band,
// with its share), and what to fix first with the way to fix it. Under the
// title, the backend's verdict on the score: everything the live Overview's
// AI Readability card said has its place here or on the Site tile.

import type { ReactNode } from "react";
import { BotIcon } from "lucide-react";
import type { AeoReadabilityComponent } from "@service/aeo";
import { clientArea, MERCHANT_V2 } from "../../merchantV2";
import { cn } from "../../lib/cn";
import { shortDate } from "../../lib/oura";
import { ArrowLink, Dot, FOCUS_RING, FoldCard, ListRow, SURFACE, TYPE, Tag } from "@oura/ui";
import { DELIVERY_ON } from "../../delivery/flag";
import { CLIENT_READABILITY_ROWS, READABILITY_ROWS, SHARE_TEXT, bandTone, clientFixAction, fixFirst, fixFirstAction, shareTone } from "./siteCopy";

/** A check's bar, in its share's tone. */
const BAR_FILL: Record<keyof typeof SHARE_TEXT, string> = { green: "bg-emerald-500", amber: "bg-amber-500", rose: "bg-rose-500" };

export function MachineReadCard({
    score,
    verdict,
    components,
    absentText,
    crawlLine,
    measuredAt,
    checkFor,
    onFix,
    onOpenFixes,
    busy,
    refresh,
    progress,
    client,
}: {
    score: number | null;
    /** The backend's verdict on the score ("Developing AI readability, clear upside"). */
    verdict?: string | null;
    /** Never re-derived here: the BE drops what does not apply, so a mean
     *  computed on this side would be a second answer to the same question. */
    components: AeoReadabilityComponent[];
    /** Why there is no score, when there is none. */
    absentText: string | null;
    /** How many pages the crawl behind the score could read. */
    crawlLine: string | null;
    /** Set only when the block was measured after the scan around it (#3831). */
    measuredAt: string | null;
    /** The audit check that found this component's gap, when one did. */
    checkFor: (key: AeoReadabilityComponent["key"]) => string | null;
    /** The How to fix drawer. Absent on the client's page, which has none. */
    onFix?: (checkKey: string) => void;
    /** The plan, for a gap no single check's drawer covers. */
    onOpenFixes?: () => void;
    busy: boolean;
    refresh?: ReactNode;
    progress?: ReactNode;
    /**
     * The client's own Website page: the checks in their words, the next fix
     * stated as ours to make, and no drawer, plan link or method note.
     */
    client?: boolean;
}) {
    const drag = fixFirst(components);
    const dragCheck = drag && onFix ? checkFor(drag.key) : null;
    const action = drag ? (client ? clientFixAction(drag) : fixFirstAction(drag)) : null;
    // The merchant's redesign shows a low share as room to grow, not as a
    // failure: amber at worst, never red.
    const calm = Boolean(client) && MERCHANT_V2;
    // The merchant sees only the checks that apply to their site (23 Oct
    // 2026), but only where the payload SAYS one does not apply: a branches
    // check with nothing to cover. A check that is absent is unknown, not
    // inapplicable (an older scan, or one the backend dropped), so it stays
    // and reads "Not measured" (Jack's review of #4646).
    const applies = (key: AeoReadabilityComponent["key"]) => {
        if (!calm) return true;
        const c = components.find((x) => x.key === key);
        return !(key === "branches" && c && c.applicable <= 0);
    };
    const shownRows = READABILITY_ROWS.filter((row) => applies(row.key));
    const toneOf = (covered: number, applicable: number) => {
        const tone = shareTone(covered, applicable);
        return calm && tone === "rose" ? "amber" : tone;
    };

    return (
        <FoldCard
            id="machine-read"
            anchor="machine-read"
            className="scroll-mt-6"
            title={calm ? "What AI can find on your site" : "What a machine can read"}
            icon={{ icon: BotIcon, area: client ? clientArea("site") : "site" }}
            foldedFact={score === null ? "Not measured" : `${score} / 100`}
            right={
                <>
                    <span>{shownRows.length} {shownRows.length === 1 ? "check" : "checks"}</span>
                    {refresh}
                </>
            }
        >
            {progress && <div className="mb-3 space-y-1 empty:hidden">{progress}</div>}
            {/* Dimmed, never blanked, while a refresh runs: these are still the
                last measured numbers, and grey boxes would say "not measured"
                about a site that is measured. */}
            <div aria-busy={busy} className={cn("transition-opacity", busy && "opacity-50")}>
                {/* Staff only in the redesign: the score is on the tile above,
                    and "major upside" is our sales read, not the client's. */}
                {score !== null && verdict && !(client && MERCHANT_V2) && (
                    <p className={cn(TYPE.tc, "m-0 mb-2 flex flex-wrap items-center gap-2 text-gray-500")}>
                        <span className="tabular-nums">AI Readability {score} / 100</span>
                        <Tag tone={bandTone(score)} className="!whitespace-normal">{verdict}</Tag>
                    </p>
                )}
                <div className={calm ? "grid grid-cols-1 gap-3 sm:grid-cols-2" : undefined}>
                    {shownRows.map((row) => {
                        const c = components.find((x) => x.key === row.key) ?? null;
                        const check = onFix && c && c.covered < c.applicable ? checkFor(row.key) : null;
                        // A one-location business is not asked the branch question (applicable 0, backend B3 #4876):
                        // nothing to find, so no verdict and nothing to fix, never a green "Found" (#4385).
                        const branchNotNeeded = row.key === "branches" && !!c && c.applicable <= 0;
                        const words = client ? CLIENT_READABILITY_ROWS[row.key] : row;
                        if (calm) {
                            // The merchant's card is a grid of small tiles (22 Oct 2026
                            // feedback): each check's result sits right by its name, and
                            // the tiles fill the card instead of one long row each.
                            const tone = c && !branchNotNeeded ? toneOf(c.covered, c.applicable) : null;
                            const [big, small] = branchNotNeeded
                                ? ["–", "Not needed"]
                                : !c
                                    ? ["–", "Not measured"]
                                    : row.key === "branches"
                                        ? [c.covered >= c.applicable ? "Found" : "Missing", null]
                                        : [`${c.score}%`, `${c.covered} of ${c.applicable} pages`];
                            const bar = c && !branchNotNeeded && row.key !== "branches" ? Math.max(0, Math.min(100, c.score)) : null;
                            return (
                                <div key={row.key} className={cn("flex flex-col rounded-xl bg-gray-50 p-4 sm:last:odd:col-span-2", (!c || branchNotNeeded) && "opacity-70")}>
                                    <p className={cn(TYPE.tb, "m-0 font-medium", c && !branchNotNeeded ? "text-gray-900" : "text-gray-500")}>{words.label}</p>
                                    <p className="m-0 mt-2 flex items-baseline gap-2">
                                        <span className={cn(TYPE.num, tone ? SHARE_TEXT[tone] : "text-gray-400")}>{big}</span>
                                        {small && <span className={cn(TYPE.tc, "tabular-nums text-gray-500")}>{small}</span>}
                                    </p>
                                    {bar !== null && tone && (
                                        <span aria-hidden className="mt-2 block h-1.5 overflow-hidden rounded-full bg-gray-200">
                                            <span className={cn("block h-full rounded-full", BAR_FILL[tone])} style={{ width: `${bar}%` }} />
                                        </span>
                                    )}
                                    <p className={cn(TYPE.tc, "m-0 mt-auto pt-3 text-gray-500")}>{words.line}</p>
                                </div>
                            );
                        }
                        return (
                            <ListRow
                                key={row.key}
                                icon={<Dot tone={c && !branchNotNeeded ? toneOf(c.covered, c.applicable) : "gray"} />}
                                title={words.label}
                                sub={words.line}
                                muted={!c || branchNotNeeded}
                                onClick={check && onFix && !branchNotNeeded ? () => onFix(check) : undefined}
                                right={
                                    branchNotNeeded ? (
                                        <span className={cn(TYPE.tc, "shrink-0 text-gray-400")}>Not needed</span>
                                    ) : c && row.key === "branches" ? (
                                        // ONE business-level check, not a branch count (backend B3 #4876: applicable is 1
                                        // for a business with several branches). "0 of 1" read as "0 of 1 branch" beside
                                        // a client's 2 outlets, so it says the verdict (#4385).
                                        <span className={cn(TYPE.tb, "shrink-0 whitespace-nowrap font-medium", SHARE_TEXT[toneOf(c.covered, c.applicable)])}>
                                            {c.covered >= c.applicable ? "Found" : "Missing"}
                                        </span>
                                    ) : c ? (
                                        <span className="shrink-0 whitespace-nowrap tabular-nums">
                                            <span className={cn(TYPE.tb, "font-medium", SHARE_TEXT[toneOf(c.covered, c.applicable)])}>
                                                {c.covered} of {c.applicable}
                                            </span>
                                            <span className={cn(TYPE.tc, "ml-1.5 text-gray-400")}>{c.score}%</span>
                                        </span>
                                    ) : (
                                        <span className={cn(TYPE.tc, "shrink-0 text-gray-400")}>Not measured</span>
                                    )
                                }
                            />
                        );
                    })}
                </div>

                {score === null ? (
                    <p className={cn(SURFACE.inner, TYPE.tc, "m-0 mt-3 text-gray-600")}>
                        <span className="font-semibold text-gray-900">Not measured on this scan.</span> {absentText}
                    </p>
                ) : (
                    action && (
                        <div className={cn(SURFACE.inner, "mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1")}>
                            <p className={cn(TYPE.tc, "m-0 text-gray-900")}>
                                <span className="font-semibold">{client ? "Next on our list:" : "Fix first:"}</span> {action.charAt(0).toLowerCase() + action.slice(1)}.
                            </p>
                            <span className="flex shrink-0 items-center gap-3">
                                {dragCheck && onFix && (
                                    <button
                                        type="button"
                                        aria-label={`How to fix: ${action}`}
                                        onClick={() => onFix(dragCheck)}
                                        className={cn(TYPE.tc, "rounded font-medium text-blue-600 hover:underline", FOCUS_RING)}
                                    >
                                        How to fix
                                    </button>
                                )}
                                {/* Fixes became Delivery, and this button kept the old
                                    name for a page that no longer exists (#3989). The
                                    label follows the same flag the rail does, as the
                                    Overview's next card already did. */}
                                {onOpenFixes && <ArrowLink onClick={onOpenFixes}>{DELIVERY_ON ? "Open Delivery" : "Open Fixes"}</ArrowLink>}
                            </span>
                        </div>
                    )
                )}

                {!client && (score !== null || crawlLine || measuredAt) && (
                    <p className={cn(TYPE.tm, "m-0 mt-3 text-gray-400")}>
                        {[
                            score !== null && `AI Readability is the mean of ${components.length} of ${READABILITY_ROWS.length} checks, each over its own pages.`,
                            crawlLine,
                            // Only when it is NOT the scan's own date: a number that
                            // disagrees with the scan beside it reads as a bug
                            // unless the card says it was measured later.
                            measuredAt && `Readability measured ${shortDate(measuredAt)}.`,
                        ].filter(Boolean).join(" ")}
                    </p>
                )}
            </div>
        </FoldCard>
    );
}
